import { useMemo, useState } from 'react';
import { useOnboardingStore } from '../store';
import { Calculator, Info } from 'lucide-react';

interface CostBreakdown {
  name: string;
  price: number;
  initialCost?: number;
  type: 'tier' | 'addon' | 'tbd' | 'discount';
  billing: 'monthly' | 'annual' | 'adjustment';
}

const AI_AUDIT_OPTION_ID = 'ai-audit-monthly';
const SEO_GBP_AUDIT_OPTION_ID = 'seo-gbp-audit-monthly';
const PAIRED_AUDIT_DISCOUNT = 10;

export function CostCalculator() {
  const { selectedTier, selectedServices } = useOnboardingStore();
  const [showBreakdown, setShowBreakdown] = useState(false);

  const { costItems, monthlyEquivalentTotal, monthlyBilledTotal, totalInitialCost, totalAnnualCost, hasAnnualPlans } = useMemo(() => {
    const items: CostBreakdown[] = [];
    let monthlyEquivalent = 0;
    let monthlyBilled = 0;
    let initialTotal = 0;
    let annualTotal = 0;
    let hasAnnual = false;

    // Add ALL selected services
    if (selectedServices && selectedServices.length > 0) {
      selectedServices.forEach(service => {
        const isAnnual = service.id.includes('-annual');

        items.push({
          name: service.name,
          price: service.monthlyFee,
          initialCost: service.initialCost,
          type: 'tier',
          billing: isAnnual ? 'annual' : 'monthly',
        });

        monthlyEquivalent += service.monthlyFee;
        if (!isAnnual) {
          monthlyBilled += service.monthlyFee;
        }

        if (service.initialCost) {
          initialTotal += service.initialCost;
        }
        if (isAnnual) {
          annualTotal += service.monthlyFee * 12;
          hasAnnual = true;
        }
      });

      const selectedIds = new Set(selectedServices.map(service => service.id));
      if (selectedIds.has(AI_AUDIT_OPTION_ID) && selectedIds.has(SEO_GBP_AUDIT_OPTION_ID)) {
        items.push({
          name: 'Paired audit price adjustment',
          price: -PAIRED_AUDIT_DISCOUNT,
          type: 'discount',
          billing: 'adjustment',
        });
        monthlyEquivalent -= PAIRED_AUDIT_DISCOUNT;
        monthlyBilled -= PAIRED_AUDIT_DISCOUNT;
      }
    } else if (selectedTier) {
      // Fallback if selectedServices is empty
      items.push({
        name: selectedTier.name,
        price: selectedTier.price,
        type: 'tier',
        billing: 'monthly',
      });
      monthlyEquivalent += selectedTier.price;
      monthlyBilled += selectedTier.price;
    }

    return {
      costItems: items,
      monthlyEquivalentTotal: Math.max(0, monthlyEquivalent),
      monthlyBilledTotal: Math.max(0, monthlyBilled),
      totalInitialCost: initialTotal,
      totalAnnualCost: annualTotal,
      hasAnnualPlans: hasAnnual,
    };
  }, [selectedTier, selectedServices]);

  if (costItems.length === 0) return null;

  return (
    <div className="bg-scale-darker border border-gray-700 rounded-lg p-4">
      <button
        onClick={() => setShowBreakdown(!showBreakdown)}
        className="w-full flex items-center justify-between text-white"
      >
        <div className="flex items-center">
          <Calculator className="w-5 h-5 text-scale-blue mr-2" />
          <span className="font-semibold">Estimated Monthly Equivalent</span>
        </div>
        <div className="text-right">
          <span className="text-xl font-bold text-scale-blue">${monthlyEquivalentTotal}</span>
        </div>
      </button>

      {showBreakdown && (
        <div className="mt-4 pt-4 border-t border-gray-700">
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-white mb-3">Selected Services:</h4>
            {costItems.filter(item => item.type !== 'tbd').map((item, index) => {
              const isAnnual = item.billing === 'annual';
              const isDiscount = item.type === 'discount';
              const annualCost = isAnnual ? item.price * 12 : null;

              return (
                <div key={index} className="mb-3 p-3 bg-scale-navy/30 rounded border border-gray-700">
                  <div className="flex justify-between items-start mb-1">
                    <span className={isDiscount ? 'text-emerald-300 font-medium' : 'text-gray-200 font-medium'}>
                      {item.name}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">{isDiscount ? 'Monthly adjustment:' : 'Monthly equivalent:'}</span>
                    <span className={isDiscount ? 'text-emerald-300 font-semibold' : 'text-scale-blue font-semibold'}>
                      {isDiscount ? `-$${Math.abs(item.price)}` : `$${item.price}`}/mo
                    </span>
                  </div>
                  {isAnnual && annualCost && (
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-gray-400">Annual Total:</span>
                      <span className="text-scale-blue font-semibold">${annualCost}/year</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>


          <div className="mt-4 pt-4 border-t-2 border-gray-600 space-y-3">
            {monthlyBilledTotal > 0 && (
              <div className="flex justify-between text-base font-bold">
                <span className="text-white">Billed Monthly:</span>
                <span className="text-scale-blue">${monthlyBilledTotal}/mo</span>
              </div>
            )}

            {hasAnnualPlans && totalAnnualCost > 0 && (
              <div className="flex justify-between text-base font-bold">
                <span className="text-white">Total Annual Cost:</span>
                <span className="text-scale-blue">${totalAnnualCost}/year</span>
              </div>
            )}

            {hasAnnualPlans && (
              <div className="flex justify-between text-base font-bold">
                <span className="text-white">Combined Monthly Equivalent:</span>
                <span className="text-scale-blue">${monthlyEquivalentTotal}/mo</span>
              </div>
            )}

            {totalInitialCost > 0 && (
              <div className="flex justify-between text-base font-bold">
                <span className="text-white">Initial Setup Cost:</span>
                <span className="text-scale-blue">${totalInitialCost}</span>
              </div>
            )}
          </div>

          {(monthlyEquivalentTotal > 0 || hasAnnualPlans) && (
            <div className="flex items-start gap-2 mt-4 text-xs text-gray-400">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>
                {monthlyBilledTotal > 0 && totalAnnualCost > 0
                  ? 'Monthly costs are billed monthly. Annual costs are billed once per year.'
                  : hasAnnualPlans
                    ? 'Annual plans are billed as a one-time payment for the full year.'
                    : 'Monthly plans are billed each month.'
                }
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
